import { create } from "zustand";
import { persist, createJSONStorage } from "zustand/middleware";
import { parseSkins, ParsedSkin } from "../lib/spineParser";

export interface RelatedTemplate {
  id: string;
  type?: string;
  config: any;
  /** 静态关联模版（bgStatic/fgStatic）无 Zip，无 Spine 二进制 */
  spineData: any | null;
  parsedSkins: ParsedSkin[];
  /** GET 模版时带下的 spineVersion，写入 drawData 中该关联模版块 */
  spineVersion?: number;
}

// 检查 Toning 中有多少 ColorSet 缺少 light/dark
export function countMissingLightDark(toning: any): number {
  return (toning.colors || []).filter((c: any) => !c.light || !c.dark).length;
}

interface ConfigStore {
  // 当前模板 ID
  currentTid: string | null;
  setCurrentTid: (tid: string | null) => void;

  // 当前模板类型
  templateType: string | null;
  setTemplateType: (type: string | null) => void;

  // 原始 Spine 数据
  spineData: any | null;
  setSpineData: (data: any | null) => void;

  // 解析后的 Skins（含 category）
  parsedSkins: ParsedSkin[];

  // Avatar 配置
  templateConfig: any | null;
  setTemplateConfig: (config: any | null) => void;

  // 所有关联模版 ID 列表
  allRelatedTemplateIds: string[];
  setAllRelatedTemplateIds: (ids: string[]) => void;

  // 已加载的关联模版数据
  loadedRelatedTemplates: RelatedTemplate[];
  setLoadedRelatedTemplates: (templates: RelatedTemplate[]) => void;
  addLoadedRelatedTemplate: (template: RelatedTemplate) => void;

  // 手动保存配置到 localStorage
  saveConfig: () => void;
  // 加载指定 tid 的配置
  loadConfig: (tid: string) => void;

  // 重置
  reset: () => void;
}

const initialState = {
  currentTid: null as string | null,
  templateType: null as string | null,
  spineData: null,
  parsedSkins: [] as ParsedSkin[],
  allRelatedTemplateIds: [] as string[],
  loadedRelatedTemplates: [] as RelatedTemplate[],
  templateConfig: null,
};

// 获取 localStorage key
const getStorageKey = (tid: string) => `neku-${tid}`;

// 从 localStorage 加载配置
const loadConfigFromStorage = (tid: string) => {
  try {
    const key = getStorageKey(tid);
    const stored = localStorage.getItem(key);
    if (stored) {
      return JSON.parse(stored);
    }
  } catch (e) {
    console.error("Failed to load config from storage:", e);
  }
  return null;
};

// 保存配置到 localStorage
const saveConfigToStorage = (tid: string, config: any) => {
  try {
    const key = getStorageKey(tid);
    // 只保存 templateConfig，预览状态不再保存在 configStore
    const storageData = {
      templateConfig: config.templateConfig,
    };
    localStorage.setItem(key, JSON.stringify(storageData));
  } catch (e) {
    console.error("Failed to save config to storage:", e);
  }
};

export const useConfigStore = create<ConfigStore>()(
  persist(
    (set, get) => ({
      ...initialState,

      setCurrentTid: (tid) => {
        const { currentTid } = get();
        if (currentTid === tid) return;

        if (currentTid) {
          const state = get();
          saveConfigToStorage(currentTid, {
            templateConfig: state.templateConfig,
          });
        }

        set({ currentTid: tid, templateType: null }); // Reset type

        if (tid) {
          const stored = loadConfigFromStorage(tid);
          if (stored) {
            set({
              templateConfig: stored.templateConfig,
            });
          } else {
            // 没有存储的配置，重置
            set({
              templateConfig: null,
            });
          }
        }
      },

      saveConfig: () => {
        const { currentTid, templateConfig } = get();
        if (!currentTid) return;

        saveConfigToStorage(currentTid, {
          templateConfig,
        });
      },

      loadConfig: (tid) => {
        const stored = loadConfigFromStorage(tid);
        if (stored) {
          set({
            currentTid: tid,
            templateConfig: stored.templateConfig,
          });
        }
      },

      setTemplateType: (type) => set({ templateType: type }),

      setSpineData: (data) => {
        // json 可能是原生 JSON 或 skelDataToJsonCompat 生成的兼容对象
        const skins = data?.json ? parseSkins(data.json) : [];
        set({ spineData: data, parsedSkins: skins });
      },

      setAllRelatedTemplateIds: (ids) => set({ allRelatedTemplateIds: ids }),
      setLoadedRelatedTemplates: (templates) =>
        set({ loadedRelatedTemplates: templates }),
      addLoadedRelatedTemplate: (template) =>
        set((state) => ({
          loadedRelatedTemplates: [...state.loadedRelatedTemplates, template],
        })),

      setTemplateConfig: (config) => {
        // 为每个 selection 选择第一个 SKU 作为默认皮肤
        const defaultSkins: string[] = [];
        if (config) {
          const skus = config.skus || {};
          for (const selection of config.selection) {
            const skuIds = selection.skuIds || [];
            if (skuIds.length > 0) {
              const firstSku = skus[skuIds[0]];
              if (firstSku?.skinName) {
                defaultSkins.push(firstSku.skinName);
              }
            }
          }
          // 确保 requireCategories 数组存在
          if (!config.requireCategories) {
            config.requireCategories = [];
          }
        }

        set({
          templateConfig: config,
        });

        // Sync to EditorStore for preview state init
        if (config) {
          const { useEditorStore } = require("./editorStore");
          useEditorStore.setState({
            currentAnimation: config?.animations[0]?.name || "",
            selectedSkins: defaultSkins,
            // isDirty is reset by loading new config
            isDirty: false,
          });
        }
      },

      reset: () => set(initialState),
    }),
    {
      name: "avatar-config-meta",
      storage: createJSONStorage(() => localStorage),
      // 只持久化 currentTid，具体配置按 tid 分开存储
      partialize: (state) => ({
        currentTid: state.currentTid,
      }),
    },
  ),
);

// 订阅状态变化，自动保存配置
useConfigStore.subscribe((state, prevState) => {
  // 如果没有 tid，不保存
  if (!state.currentTid) return;

  // templateConfig 变化才需要保存到数据库
  const templateConfigChanged =
    state.templateConfig !== prevState.templateConfig;

  // 保存到本地存储（预览状态和配置都保存）
  if (templateConfigChanged && state.templateConfig) {
    saveConfigToStorage(state.currentTid, {
      templateConfig: state.templateConfig,
    });
  }
});
